import {useCallback, useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {
    getDuplicatedItemData,
    isNew,
    useDuplicateFormId,
} from 'services/utils/editForm'
import {
    useGetPublicationFolderQuery,
    useSavePublicationFolderMutation,
    useCreatePublicationFolderMutation,
} from 'store/slices/api/publicationFoldersApi'
import {useI18n} from 'store/Store'
import {useAlert} from 'components/Display/Alert/AlertContext'
import {useNavigate} from 'react-router-dom'
import {yupResolver} from '@hookform/resolvers/yup'
import {publicationFolderSchema} from 'services/validations/schemas/publication'
import {useUploadImageMutation} from 'store/slices/api/uploadsApi'
import {urls} from 'services/utils/urls'

const resolveExcelUrlFromUploadLink = (link, folderName) => {
    if (!link || typeof link !== 'string') return ''
    if (/^https?:\/\//i.test(link)) return link
    const trimmed = link.replace(/^\//, '')
    if (trimmed.startsWith('assets/')) {
        return `https://rokdim.co.il/${trimmed}`
    }
    const fileName = trimmed.split('/').filter(Boolean).pop()
    return `${urls.base}/${folderName}/${fileName}`
}

const usePublicationFolderForm = (publicationFolderId) => {
    const {i18n} = useI18n()
    const {showAlert} = useAlert()
    const navigate = useNavigate()
    const isNewPublicationFolder = isNew(publicationFolderId)
    const duplicateFromId = useDuplicateFormId(publicationFolderId)
    const formPublicationFolderId = isNewPublicationFolder
        ? duplicateFromId
        : publicationFolderId

    const [formError, setFormError] = useState(null)
    const [excelUploading, setExcelUploading] = useState(false)

    const [doUploadImage] = useUploadImageMutation()
    const [doCreatePublicationFolder] = useCreatePublicationFolderMutation()
    const [doSavePublicationFolder] = useSavePublicationFolderMutation()

    const {data: publicationFolder} = useGetPublicationFolderQuery(
        formPublicationFolderId,
        {
            skip: !formPublicationFolderId,
        }
    )

    const form = useForm({
        defaultValues: {isActive: true},
        mode: 'onSubmit',
        resolver: yupResolver(publicationFolderSchema),
    })

    useEffect(() => {
        if (publicationFolder) {
            form.reset(
                isNewPublicationFolder
                    ? getDuplicatedItemData(publicationFolder)
                    : publicationFolder
            )
        }
    }, [publicationFolder, isNewPublicationFolder, form])

    const onSubmit = async (values) => {
        let response
        const body = {
            ...values,
        }
        if (values._id) {
            response = await doSavePublicationFolder(body)
        } else {
            response = await doCreatePublicationFolder(body)
        }

        if (response?.data?._id) {
            const message = i18n.t('form.savedSuccessfully', {
                itemName: i18n.t('tables.publicationFolder'),
            })
            navigate(`/publicationFolders/${response.data._id}`)
            showAlert(message)
        }
        if (response.error) {
            setFormError(response.error.data.error)
        }
    }

    const submit = form.handleSubmit(onSubmit)

    const uploadExcelFile = useCallback(
        async (file) => {
            if (!file) return
            const folderName = urls.publicationFoldersExcel
            setExcelUploading(true)
            try {
                const formData = new FormData()
                formData.append('files', file)
                formData.append('folderName', folderName)

                const response = await doUploadImage({
                    formData,
                    folderName,
                })

                if (response.error) {
                    showAlert(i18n.t('publicationFolders.uploadExcelFailed'))
                    return
                }

                const link = response?.data?.files?.[0]?.link
                const excelUrlValue = resolveExcelUrlFromUploadLink(
                    link,
                    folderName
                )

                if (excelUrlValue) {
                    form.setValue('excelUrl', excelUrlValue, {
                        shouldDirty: true,
                        shouldValidate: true,
                    })
                    showAlert(i18n.t('publicationFolders.uploadExcelSuccess'))
                } else {
                    showAlert(i18n.t('publicationFolders.uploadExcelFailed'))
                }
            } finally {
                setExcelUploading(false)
            }
        },
        [doUploadImage, form, i18n, showAlert]
    )

    return {
        form,
        formError,
        publicationFolder,
        submit,
        uploadExcelFile,
        excelUploading,
    }
}

export default usePublicationFolderForm
